import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import { BannerState, WANT_TARGETS } from './setupState';

// Every string of the Setup Banner (Asana 1218957945501621, Setup Banner States
// block), verbatim where the ticket gives one. Point values are NOT in these
// strings: the components render them through <Points>, so a piece of copy that
// contains a value is split into parts around it.

export interface CopyParts {
  tag: string;
  title: string;
  /** Body sentence; `bodyPoints` marks where a point value is inserted, with `bodyAfter` following it */
  body: string;
  bodyPoints?: number;
  bodyAfter?: string;
  button: string;
  secondaryButton?: string;
  hint?: string;
  /** Sentence under the Claim button in state C */
  claimHint?: string;
}

/** Chip labels, short, the ticket's descriptive names. */
export const CHIP_LABELS: Record<OnboardingTaskKey, string> = {
  VERIFY_PHONE: 'Verify mobile',
  VERIFY_EMAIL: 'Verify email',
  ADD_ADDRESS: 'Shipping address',
  ADD_WANT_1: '1 want',
  ADD_WANT_10: '10 wants',
  ADD_WANT_50: '50 wants',
  SEND_FIRST_TRADE: 'Send first trade',
  FOLLOW_TWITTER: 'Follow on X',
  FOLLOW_BLUESKY: 'Follow on Bluesky',
  COMPLETE_FIRST_TRADE: 'Complete first trade',
};

export const LOCKED_NEEDS_ADDRESS = 'needs address';
export const WAITING_CHIP_LABEL = 'First trade · in the mail';

export const TAGS: Record<BannerState | 'picked', string> = {
  A: 'STEP 1 · UNLOCKS THE OTHER NINE',
  B: 'SUGGESTED NEXT · OR PICK ANY ON THE RIGHT',
  C: 'REWARDS READY',
  D: 'KEEP GOING',
  E: 'UNLOCKS YOUR FIRST TRADE',
  F: 'WHILE YOUR TRADE IS IN THE MAIL',
  G: 'LAST ONE · NOTHING FOR YOU TO DO',
  H: 'SETUP COMPLETE',
  I: 'OPTIONAL · ONLY SOCIAL TASKS LEFT',
  gone: '',
  picked: 'YOUR PICK',
};

export const BACK_TO_SUGGESTION = 'Back to suggestion';
export const CLAIM_ALL = 'Claim all';
export const CLAIM = 'Claim';
export const CLAIM_ONE_BY_ONE = 'Or claim them one by one on the right';
export const HIDE_LINK = "I'm not on social media, hide this";
export const HIDE_FOOTNOTE_BEFORE = 'Hiding is permanent. Nothing on the homepage asks again, but the ';
export const HIDE_FOOTNOTE_AFTER = ' stays available from your profile menu.';
export const HIDDEN_TOAST = 'Setup card hidden. You can bring it back from your profile menu any time.';
export const UNDO = 'Undo';
export const SETUP_COMPLETE_TITLE = 'Setup complete';
export const SETUP_COMPLETE_BODY_BEFORE = "You've earned the full ";
export const SETUP_COMPLETE_BODY_AFTER = '. This card goes away on your next visit.';
export const WAITING_FOR_YOU = 'Waiting for you';
export const STILL_WAITING_FOR_YOU = 'Still waiting for you';
export const EARNED_FROM_SETUP = 'Earned from setup';
export const CHIP_HELP = 'Click any open task to make it the one on the left. The task you swapped out goes back into the list.';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Rule-4 tasks: the copy each open task shows when it is the one on the left. */
const TASK_COPY: Partial<Record<OnboardingTaskKey, Omit<CopyParts, 'tag'>>> = {
  VERIFY_PHONE: {
    title: 'Verify your mobile number',
    body: "One text message. It's how traders know a real person is on the other end of the envelope.",
    button: 'Verify mobile number',
    hint: 'About a minute',
  },
  VERIFY_EMAIL: {
    title: 'Verify your email',
    body: 'A single link in your inbox. The quickest one left.',
    button: 'Send verification link',
    hint: 'About 30 seconds',
  },
  FOLLOW_TWITTER: {
    title: 'Follow us on X',
    body: 'Follow us on X for TradeMagic news, new features, and giveaways.',
    button: 'Follow on X',
  },
  FOLLOW_BLUESKY: {
    title: 'Follow us on Bluesky',
    body: 'Follow us on Bluesky for TradeMagic news, new features, and giveaways.',
    button: 'Follow on Bluesky',
  },
  ADD_WANT_1: {
    title: 'Add your first card to your want list',
    body: 'One card is enough for the My want list filter to start catching it here.',
    button: 'Add a card',
    hint: 'Search, or paste a decklist',
  },
  ADD_ADDRESS: {
    title: 'Add your shipping address',
    body: 'Other traders need somewhere to send cards. It also unlocks the two trade tasks, worth another ',
    bodyPoints: 400,
    bodyAfter: ' between them.',
    button: 'Add address',
    hint: 'Never shown publicly',
  },
  SEND_FIRST_TRADE: {
    title: 'Send your first trade',
    body: 'Pick a card another trader wants and send it. The reward lands when the package is on its way.',
    button: 'Send cards',
    hint: 'About five minutes',
  },
};

const wantThresholdCopy = (key: OnboardingTaskKey, wantListCount: number): Omit<CopyParts, 'tag'> => {
  const target = WANT_TARGETS[key] ?? 50;
  const left = Math.max(target - wantListCount, 0);
  const reward = key === 'ADD_WANT_50' ? 'the biggest list reward' : 'the next list reward';
  return {
    title: `${left} more ${plural(left, 'card', 'cards')} on your want list`,
    body: `You're ${wantListCount} ${plural(wantListCount, 'card', 'cards')} into ${reward}. More wants also mean more matches with other traders.`,
    button: 'Add cards',
    hint: 'Search, or paste a decklist',
  };
};

export interface CopyContext {
  state: BannerState;
  taskKey: OnboardingTaskKey | null;
  picked: boolean;
  readyKeys: OnboardingTaskKey[];
  tasks: OnboardingTask[];
  wantListCount: number;
  /** State G: the recipient of the first trade */
  counterpartyUsername?: string | null;
  /** States F and G: days until the estimated delivery, or the weekday label */
  arrivesHint?: string | null;
}

/** The left column's copy for the featured pick. */
export const featuredCopy = (ctx: CopyContext): CopyParts => {
  const { state, taskKey, picked, readyKeys, wantListCount } = ctx;
  const readyWants = readyKeys.filter((key) => key in WANT_TARGETS);
  switch (state) {
    case 'A':
      return { tag: TAGS.A, ...TASK_COPY.VERIFY_PHONE! };
    case 'C': {
      const n = readyKeys.length;
      // The decklist title only when every reward waiting is a want-list threshold (review 2026-09-28)
      const decklist = n > 0 && readyWants.length === n;
      return {
        tag: `${n} ${plural(n, 'REWARD', 'REWARDS')} READY`,
        title: decklist ? 'Your decklist import paid off' : 'You have points waiting',
        body: decklist
          ? `You pasted ${wantListCount} cards into your want list, which completes the ${readyWants.length === 1 ? 'list' : readyWants.length === 2 ? 'two list' : '1-, 10- and 50-card'} ${plural(readyWants.length, 'task', 'tasks')} in one go. Claim ${plural(n, 'it and it goes', 'them together and they go')} straight into your balance.`
          : `You finished ${n} ${plural(n, 'task', 'tasks')}. Claim ${plural(n, 'it and it goes', 'them together and they go')} straight into your balance.`,
        button: n === 1 ? CLAIM : CLAIM_ALL,
        claimHint: n > 1 ? CLAIM_ONE_BY_ONE : undefined,
      };
    }
    case 'D':
      return { tag: TAGS.D, ...wantThresholdCopy(taskKey ?? 'ADD_WANT_50', wantListCount) };
    case 'E':
      return { tag: TAGS.E, ...TASK_COPY.ADD_ADDRESS! };
    case 'G':
      return {
        tag: TAGS.G,
        title: 'Your first trade is in the mail',
        body: `It's on its way to ${ctx.counterpartyUsername ?? 'your trade partner'}. The final `,
        bodyPoints: 100,
        bodyAfter: ' unlocks when they mark it received.',
        button: 'Track package',
        hint: ctx.arrivesHint ?? undefined,
      };
    case 'H':
      return {
        tag: TAGS.H,
        title: 'That was the last one',
        body: "Claim it and you've earned the full ",
        bodyPoints: 1600,
        bodyAfter: '. This card goes away after you claim, and the space goes to your activity.',
        button: CLAIM,
      };
    case 'I': {
      const both = readyKeys.length === 0 && ctx.tasks.filter((t) => (t.taskKey === 'FOLLOW_TWITTER' || t.taskKey === 'FOLLOW_BLUESKY') && t.status === 'AVAILABLE').length === 2;
      return {
        tag: TAGS.I,
        title: both ? 'Two quick follows left' : 'One quick follow left',
        body: "That's every trading task done. If you're on X or Bluesky, following us gets you TradeMagic news, new features, and giveaways.",
        button: 'Follow on X',
        secondaryButton: 'Follow on Bluesky',
      };
    }
    case 'B':
    case 'F':
    default: {
      const key = taskKey ?? 'VERIFY_EMAIL';
      const base = key in WANT_TARGETS && key !== 'ADD_WANT_1' ? wantThresholdCopy(key, wantListCount) : TASK_COPY[key] ?? TASK_COPY.VERIFY_EMAIL!;
      const tag = picked ? TAGS.picked : state === 'F' ? TAGS.F : TAGS.B;
      const hint = state === 'F' && ctx.arrivesHint ? ctx.arrivesHint : base.hint;
      return { tag, ...base, hint };
    }
  }
};

/** "Arrives in about {n} days" for state F; "Expected {weekday}" for state G. */
export const arrivesHint = (estimatedDeliveryBy: string | null | undefined, state: BannerState, now: Date = new Date()): string | null => {
  if (!estimatedDeliveryBy) return null;
  const eta = new Date(estimatedDeliveryBy);
  if (Number.isNaN(eta.getTime())) return null;
  if (state === 'G') {
    return `Expected ${eta.toLocaleDateString('en-US', { weekday: 'long' })}`;
  }
  const days = Math.max(1, Math.round((eta.getTime() - now.getTime()) / 86400000));
  return `Arrives in about ${days} ${plural(days, 'day', 'days')}`;
};
